import React, { useState } from 'react';

// USCIS Notice Scanner - Step 7: Evidence Gap Analysis & Prioritization
export const USCISStep7GapAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [gapAnalysis, setGapAnalysis] = useState(formData.gapAnalysis || null);
  const [analyzing, setAnalyzing] = useState(false);

  const performGapAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const documentAnalysis = formData.documentAnalysis || {};
      const breakdown = formData.breakdown || {};
      const uploadedDocs = formData.responseDocuments || [];
      const parsedResults = formData.parsedResults || {};
      
      // Debug: Log the actual data we're working with
      console.log('📊 Gap Analysis Debug Data:');
      console.log('- Upload Documents:', uploadedDocs.length);
      console.log('- Document Analysis:', documentAnalysis);
      console.log('- Parsed Results:', parsedResults);
      
      // Show actual uploaded documents for debugging
      uploadedDocs.forEach((doc, index) => {
        console.log(`Document ${index + 1}:`, {
          name: doc.name,
          category: doc.category,
          hasAnalysis: !!doc.analysis,
          redFlags: doc.red_flags || [],
          caseRelevance: doc.case_relevance || 'Not analyzed',
          immigrationValue: doc.immigration_value || 'Not assessed'
        });
      });
      
      // Extract actual analysis findings from uploaded documents
      const documentFindings = extractDocumentFindings(uploadedDocs);
      const noticeAnalysis = extractNoticeAnalysis(parsedResults);
      
      console.log('📋 Extracted Document Findings:', documentFindings);
      console.log('📋 Notice Analysis:', noticeAnalysis);
      
      // Generate comprehensive gap analysis based on actual data
      const gaps = generateComprehensiveGapAnalysis(
        documentFindings,
        noticeAnalysis,
        breakdown,
        documentAnalysis,
        uploadedDocs
      );

      console.log('📊 Generated Gap Analysis:', gaps);
      setGapAnalysis(gaps);
      setFormData({ ...formData, gapAnalysis: gaps });
    } catch (error) {
      console.error('Gap analysis error:', error);
    }
    
    setAnalyzing(false);
  };

  // Extract findings from document analysis results
  const extractDocumentFindings = (documents) => {
    const findings = {
      red_flags: [],
      gaps: [],
      inconsistencies: [],
      quality_issues: [],
      missing_evidence: [],
      authenticity_concerns: [],
      document_insights: []
    };

    console.log(`🔍 Extracting findings from ${documents.length} documents`);

    documents.forEach((doc, index) => {
      console.log(`Processing document ${index + 1}: ${doc.name}`);
      
      // Extract red flags from document analysis
      if (doc.red_flags && Array.isArray(doc.red_flags) && doc.red_flags.length > 0) {
        doc.red_flags.forEach(flag => {
          if (flag && flag.trim()) {
            findings.red_flags.push({
              source: doc.name,
              category: doc.categoryLabel || doc.category,
              issue: flag,
              severity: 'High',
              document_id: doc.id
            });
          }
        });
      }

      // Check for analysis errors (OCR failures, etc.)
      if (doc.analysis_error) {
        findings.quality_issues.push({
          source: doc.name,
          category: doc.categoryLabel || doc.category,
          issue: doc.analysis_error,
          severity: 'Medium',
          document_id: doc.id
        });
      }

      // Check for low-value evidence
      if (doc.immigration_value === 'Low') {
        findings.gaps.push({
          source: doc.name,
          category: doc.categoryLabel || doc.category,
          issue: `Document "${doc.name}" provides limited immigration value`,
          severity: 'Low',
          document_id: doc.id
        });
      }

      // Check for missing authenticity indicators
      if (doc.authenticity_indicators && Array.isArray(doc.authenticity_indicators) && doc.authenticity_indicators.length === 0) {
        findings.authenticity_concerns.push({
          source: doc.name,
          category: doc.categoryLabel || doc.category,
          issue: 'No authenticity indicators found in document',
          severity: 'Medium',
          document_id: doc.id
        });
      }

      // Extract case relevance insights
      if (doc.case_relevance) {
        findings.document_insights.push({
          source: doc.name,
          category: doc.categoryLabel || doc.category,
          insight: doc.case_relevance,
          value: doc.immigration_value || 'Not assessed',
          document_id: doc.id
        });
      }

      // Check for content description insights
      if (doc.content_description) {
        findings.document_insights.push({
          source: doc.name,
          category: doc.categoryLabel || doc.category,
          insight: `Document content: ${doc.content_description}`,
          value: doc.immigration_value || 'Not assessed',
          document_id: doc.id
        });
      }
    });

    console.log('📋 Final findings extracted:', {
      red_flags: findings.red_flags.length,
      quality_issues: findings.quality_issues.length,
      gaps: findings.gaps.length,
      authenticity_concerns: findings.authenticity_concerns.length,
      document_insights: findings.document_insights.length
    });

    return findings;
  };

  // Extract analysis from USCIS notice
  const extractNoticeAnalysis = (parsedResults) => {
    const analysis = {
      main_issues: parsedResults.main_issues || [],
      required_evidence: parsedResults.required_evidence || parsedResults.required_exhibits || [],
      severity_assessment: parsedResults.severity_assessment || 'Manual review required',
      urgency_level: parsedResults.urgency_level || 'Medium',
      consequences: parsedResults.consequences || 'Manual review required',
      legal_standards: parsedResults.legal_standards || [],
      citations: parsedResults.citations || []
    };

    return analysis;
  };

  // Generate comprehensive gap analysis
  const generateComprehensiveGapAnalysis = (documentFindings, noticeAnalysis, breakdown, documentAnalysis, uploadedDocs) => {
    const gaps = {
      evidence_gaps: [],
      red_flags_summary: [],
      inconsistencies_found: [],
      quality_concerns: [],
      missing_critical_evidence: [],
      document_insights: [], // New section for document insights
      impact_scoring: {
        high_impact_gaps: 0,
        medium_impact_gaps: 0,
        low_impact_gaps: 0
      },
      evidence_hierarchy: {
        must_have: [],
        should_have: [],
        nice_to_have: []
      },
      completion_status: {
        overall_completion: '0%',
        critical_items_completion: '0%',
        estimated_time_needed: 'Assessment required'
      },
      actionable_recommendations: []
    };

    console.log('🔧 Generating comprehensive gap analysis...');

    // Add uploaded document insights to show actual analysis
    if (documentFindings.document_insights && documentFindings.document_insights.length > 0) {
      gaps.document_insights = documentFindings.document_insights.map(insight => ({
        id: Date.now() + Math.random(),
        category: `Document Analysis: ${insight.category}`,
        impact: insight.value === 'High' ? 'High' : insight.value === 'Medium' ? 'Medium' : 'Low',
        priority: insight.value === 'High' ? 'Priority 1' : 'Priority 2',
        description: insight.insight,
        source_document: insight.source,
        urgency: insight.value === 'High' ? 'Critical' : 'Important',
        actionable_items: [
          `Document "${insight.source}" has been analyzed`,
          `Immigration value assessed as: ${insight.value}`,
          'Consider strengthening this evidence if value is low'
        ]
      }));
    }

    // Process red flags from document analysis
    documentFindings.red_flags.forEach(flag => {
      gaps.red_flags_summary.push({
        id: Date.now() + Math.random(),
        category: `Document Issue: ${flag.category}`,
        impact: flag.severity,
        priority: flag.severity === 'High' ? 'Priority 1' : 'Priority 2',
        description: flag.issue,
        source_document: flag.source,
        urgency: flag.severity === 'High' ? 'Critical' : 'Important',
        actionable_items: [
          `Review ${flag.source} for the identified issue`,
          'Consider obtaining a replacement document if necessary',
          'Ensure document authenticity and completeness'
        ]
      });
    });

    // Process quality issues
    documentFindings.quality_issues.forEach(issue => {
      gaps.quality_concerns.push({
        id: Date.now() + Math.random(),
        category: 'Document Quality',
        impact: issue.severity,
        priority: 'Priority 2',
        description: issue.issue,
        source_document: issue.source,
        urgency: 'Important',
        actionable_items: [
          'Re-upload document with better quality',
          'Ensure document is clearly readable',
          'Consider professional document preparation'
        ]
      });
    });

    // Process authenticity concerns
    documentFindings.authenticity_concerns.forEach(concern => {
      gaps.quality_concerns.push({
        id: Date.now() + Math.random(),
        category: 'Document Authenticity',
        impact: concern.severity,
        priority: 'Priority 2',
        description: concern.issue,
        source_document: concern.source,
        urgency: 'Important',
        actionable_items: [
          'Verify document authenticity',
          'Ensure document contains proper official markings',
          'Consider obtaining certified copies'
        ]
      });
    });

    // Analyze coverage against USCIS requirements
    const requiredEvidence = noticeAnalysis.required_evidence || [];
    const uploadedCategories = [...new Set(uploadedDocs.map(doc => doc.category))];
    
    // Check for missing evidence based on USCIS requirements
    const evidenceGaps = [];
    requiredEvidence.forEach(requirement => {
      if (requirement && requirement !== 'Not found in text' && requirement !== 'Manual review required') {
        // Simple matching logic to identify gaps
        const hasMatchingDoc = uploadedDocs.some(doc => {
          const reqLower = requirement.toLowerCase();
          const categoryLower = (doc.categoryLabel || doc.category || '').toLowerCase();
          const relevanceLower = (doc.case_relevance || '').toLowerCase();
          
          return reqLower.includes(categoryLower.split(' ')[0]) || 
                 relevanceLower.includes(reqLower.split(' ')[0]);
        });
        
        if (!hasMatchingDoc) {
          evidenceGaps.push({
            id: Date.now() + Math.random(),
            category: `Missing: ${requirement}`,
            impact: 'High',
            priority: 'Priority 1',
            description: `Required evidence not provided: ${requirement}`,
            missing_items: [requirement],
            current_coverage: '0%',
            required_coverage: '100%',
            urgency: 'Critical',
            actionable_items: [
              `Obtain and upload: ${requirement}`,
              'Verify document authenticity',
              'Ensure document completeness'
            ]
          });
        }
      }
    });

    gaps.evidence_gaps = evidenceGaps;

    // Process main issues from USCIS notice
    if (noticeAnalysis.main_issues && Array.isArray(noticeAnalysis.main_issues)) {
      noticeAnalysis.main_issues.forEach(issue => {
        if (issue && issue !== 'Not found in text' && issue !== 'Manual review required') {
          gaps.missing_critical_evidence.push({
            id: Date.now() + Math.random(),
            category: 'USCIS Concern',
            impact: 'High',
            priority: 'Priority 1',
            description: issue,
            urgency: 'Critical',
            actionable_items: [
              'Address this specific USCIS concern',
              'Provide comprehensive evidence',
              'Consider legal consultation if needed'
            ]
          });
        }
      });
    }

    // Calculate impact scoring
    const allIssues = [
      ...gaps.red_flags_summary,
      ...gaps.quality_concerns,
      ...gaps.evidence_gaps,
      ...gaps.missing_critical_evidence,
      ...gaps.document_insights
    ];

    gaps.impact_scoring = {
      high_impact_gaps: allIssues.filter(issue => issue.impact === 'High').length,
      medium_impact_gaps: allIssues.filter(issue => issue.impact === 'Medium').length,
      low_impact_gaps: allIssues.filter(issue => issue.impact === 'Low').length
    };

    // Generate evidence hierarchy
    const filteredRequired = requiredEvidence.filter(req => 
      req && req !== 'Not found in text' && req !== 'Manual review required'
    );
    
    gaps.evidence_hierarchy = {
      must_have: filteredRequired.slice(0, 3),
      should_have: filteredRequired.slice(3, 6),
      nice_to_have: filteredRequired.slice(6, 9)
    };

    // Calculate completion status
    const totalRequired = filteredRequired.length;
    const totalProvided = uploadedDocs.length;
    const completionPercentage = totalRequired > 0 ? Math.round((totalProvided / totalRequired) * 100) : 0;
    
    gaps.completion_status = {
      overall_completion: `${Math.min(completionPercentage, 100)}%`,
      critical_items_completion: `${Math.max(0, completionPercentage - 20)}%`,
      estimated_time_needed: allIssues.length > 5 ? '3-4 weeks' : allIssues.length > 2 ? '2-3 weeks' : '1-2 weeks'
    };

    // Generate actionable recommendations
    const recommendations = [];
    if (gaps.red_flags_summary.length > 0) {
      recommendations.push('Address all high-priority red flags first');
    }
    if (gaps.evidence_gaps.length > 0) {
      recommendations.push('Obtain missing critical evidence identified by USCIS');
    }
    if (gaps.quality_concerns.length > 0) {
      recommendations.push('Improve document quality for better analysis');
    }
    if (uploadedDocs.length > 0) {
      recommendations.push(`Continue building evidence portfolio (${uploadedDocs.length} documents uploaded)`);
    }
    recommendations.push('Consider professional document review if issues persist');

    gaps.actionable_recommendations = recommendations;

    console.log('📊 Gap analysis generated:', {
      total_issues: allIssues.length,
      red_flags: gaps.red_flags_summary.length,
      quality_concerns: gaps.quality_concerns.length,
      evidence_gaps: gaps.evidence_gaps.length,
      document_insights: gaps.document_insights.length
    });

    return gaps;
  };

  React.useEffect(() => {
    if (!gapAnalysis) {
      performGapAnalysis();
    }
  }, []);

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High': return 'text-red-400 bg-red-900/20 border-red-500';
      case 'Medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500';
      case 'Low': return 'text-green-400 bg-green-900/20 border-green-500';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-500';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Priority 1': return '🔴';
      case 'Priority 2': return '🟡';
      case 'Priority 3': return '🔵';
      default: return '⚪';
    }
  };

  const getCoverageColor = (coverage) => {
    const percent = parseInt(coverage) || 0;
    if (percent >= 80) return 'text-green-400';
    if (percent >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📊 Evidence Gap Analysis & Prioritization</h3>
        <p className="text-gray-400 text-lg">Detailed analysis of gaps, red flags, and inconsistencies from your documents</p>
      </div>

      {analyzing ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-pulse text-6xl mb-4">🔍</div>
          <h4 className="text-xl text-white mb-2">Analyzing Evidence Gaps</h4>
          <p className="text-gray-400">Processing OCR results and identifying missing evidence...</p>
        </div>
      ) : gapAnalysis && (
        <>
          {/* Completion Status Overview */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📈 Evidence Completion Status</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className={`text-3xl font-bold ${getCoverageColor(gapAnalysis.completion_status.overall_completion)}`}>
                  {gapAnalysis.completion_status.overall_completion}
                </div>
                <p className="text-gray-400">Overall Completion</p>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold ${getCoverageColor(gapAnalysis.completion_status.critical_items_completion)}`}>
                  {gapAnalysis.completion_status.critical_items_completion}
                </div>
                <p className="text-gray-400">Critical Items</p>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-blue-400">{gapAnalysis.completion_status.estimated_time_needed}</div>
                <p className="text-gray-400">Time Needed</p>
              </div>
            </div>
          </div>

          {/* Impact Summary */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">⚠️ Issues Impact Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-400">{gapAnalysis.impact_scoring.high_impact_gaps}</div>
                <p className="text-red-400">High Impact Issues</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400">{gapAnalysis.impact_scoring.medium_impact_gaps}</div>
                <p className="text-yellow-400">Medium Impact Issues</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">{gapAnalysis.impact_scoring.low_impact_gaps}</div>
                <p className="text-green-400">Low Impact Issues</p>
              </div>
            </div>
          </div>

          {/* Document Insights from Uploaded Files */}
          {gapAnalysis.document_insights && gapAnalysis.document_insights.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white">📄 Your Uploaded Documents Analysis</h4>
              <p className="text-gray-400 mb-4">Analysis results from your uploaded documents and their relevance to your case</p>
              {gapAnalysis.document_insights.map((insight) => (
                <div key={insight.id} className={`p-6 rounded-lg border-2 ${getImpactColor(insight.impact)}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getPriorityIcon(insight.priority)}</span>
                      <div>
                        <h5 className="text-lg font-semibold text-white">{insight.category}</h5>
                        <p className="text-gray-400 text-sm">{insight.description}</p>
                        <p className="text-blue-400 text-sm mt-1">Source: {insight.source_document}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        insight.impact === 'High' ? 'bg-green-600 text-white' :
                        insight.impact === 'Medium' ? 'bg-yellow-600 text-white' :
                        'bg-blue-600 text-white'
                      }`}>
                        {insight.impact} Value
                      </span>
                      <p className="text-gray-400 text-sm mt-1">{insight.priority}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-white font-medium mb-2">📋 Analysis Results:</p>
                    <ul className="space-y-1">
                      {insight.actionable_items.map((item, index) => (
                        <li key={index} className="text-gray-300 text-sm flex items-start">
                          <span className="text-green-400 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Red Flags from Document Analysis */}
          {gapAnalysis.red_flags_summary && gapAnalysis.red_flags_summary.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white">🚨 Red Flags from Document Analysis</h4>
              {gapAnalysis.red_flags_summary.map((flag) => (
                <div key={flag.id} className={`p-6 rounded-lg border-2 ${getImpactColor(flag.impact)}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getPriorityIcon(flag.priority)}</span>
                      <div>
                        <h5 className="text-lg font-semibold text-white">{flag.category}</h5>
                        <p className="text-gray-400 text-sm">{flag.description}</p>
                        <p className="text-blue-400 text-sm mt-1">Source: {flag.source_document}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        flag.impact === 'High' ? 'bg-red-600 text-white' :
                        flag.impact === 'Medium' ? 'bg-yellow-600 text-white' :
                        'bg-green-600 text-white'
                      }`}>
                        {flag.impact} Impact
                      </span>
                      <p className="text-gray-400 text-sm mt-1">{flag.priority}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-white font-medium mb-2">📋 Recommended Actions:</p>
                    <ul className="space-y-1">
                      {flag.actionable_items.map((item, index) => (
                        <li key={index} className="text-gray-300 text-sm flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Missing Evidence Gaps */}
          {gapAnalysis.evidence_gaps && gapAnalysis.evidence_gaps.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white">📋 Missing Evidence Gaps</h4>
              {gapAnalysis.evidence_gaps.map((gap) => (
                <div key={gap.id} className={`p-6 rounded-lg border-2 ${getImpactColor(gap.impact)}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getPriorityIcon(gap.priority)}</span>
                      <div>
                        <h5 className="text-lg font-semibold text-white">{gap.category}</h5>
                        <p className="text-gray-400 text-sm">{gap.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        gap.impact === 'High' ? 'bg-red-600 text-white' :
                        gap.impact === 'Medium' ? 'bg-yellow-600 text-white' :
                        'bg-green-600 text-white'
                      }`}>
                        {gap.impact} Impact
                      </span>
                      <p className="text-gray-400 text-sm mt-1">{gap.priority}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-white font-medium mb-2">📋 Missing Items:</p>
                      <ul className="space-y-1">
                        {gap.missing_items.map((item, index) => (
                          <li key={index} className="text-gray-300 text-sm flex items-start">
                            <span className="text-red-400 mr-2">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <p className="text-white font-medium mb-2">📊 Coverage Status:</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400 text-sm">Current:</span>
                          <span className={`font-bold ${getCoverageColor(gap.current_coverage)}`}>
                            {gap.current_coverage}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400 text-sm">Required:</span>
                          <span className="text-white font-bold">{gap.required_coverage}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              parseInt(gap.current_coverage) >= 80 ? 'bg-green-500' :
                              parseInt(gap.current_coverage) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: gap.current_coverage }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quality Concerns */}
          {gapAnalysis.quality_concerns && gapAnalysis.quality_concerns.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white">📄 Document Quality Concerns</h4>
              {gapAnalysis.quality_concerns.map((concern) => (
                <div key={concern.id} className={`p-6 rounded-lg border-2 ${getImpactColor(concern.impact)}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getPriorityIcon(concern.priority)}</span>
                      <div>
                        <h5 className="text-lg font-semibold text-white">{concern.category}</h5>
                        <p className="text-gray-400 text-sm">{concern.description}</p>
                        <p className="text-blue-400 text-sm mt-1">Source: {concern.source_document}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        concern.impact === 'High' ? 'bg-red-600 text-white' :
                        concern.impact === 'Medium' ? 'bg-yellow-600 text-white' :
                        'bg-green-600 text-white'
                      }`}>
                        {concern.impact} Impact
                      </span>
                      <p className="text-gray-400 text-sm mt-1">{concern.priority}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-white font-medium mb-2">📋 Recommended Actions:</p>
                    <ul className="space-y-1">
                      {concern.actionable_items.map((item, index) => (
                        <li key={index} className="text-gray-300 text-sm flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* USCIS Specific Concerns */}
          {gapAnalysis.missing_critical_evidence && gapAnalysis.missing_critical_evidence.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white">🏛️ USCIS Specific Concerns</h4>
              {gapAnalysis.missing_critical_evidence.map((concern) => (
                <div key={concern.id} className={`p-6 rounded-lg border-2 ${getImpactColor(concern.impact)}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getPriorityIcon(concern.priority)}</span>
                      <div>
                        <h5 className="text-lg font-semibold text-white">{concern.category}</h5>
                        <p className="text-gray-400 text-sm">{concern.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        concern.impact === 'High' ? 'bg-red-600 text-white' :
                        concern.impact === 'Medium' ? 'bg-yellow-600 text-white' :
                        'bg-green-600 text-white'
                      }`}>
                        {concern.impact} Impact
                      </span>
                      <p className="text-gray-400 text-sm mt-1">{concern.priority}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-white font-medium mb-2">📋 Recommended Actions:</p>
                    <ul className="space-y-1">
                      {concern.actionable_items.map((item, index) => (
                        <li key={index} className="text-gray-300 text-sm flex items-start">
                          <span className="text-blue-400 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Evidence Hierarchy */}
          {gapAnalysis.evidence_hierarchy && (
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-white mb-4">📊 Evidence Priority Hierarchy</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-red-900/20 border border-red-500 p-4 rounded-lg">
                  <h5 className="text-red-400 font-semibold mb-3 flex items-center">
                    <span className="mr-2">🔴</span>
                    Must Have
                  </h5>
                  <ul className="space-y-1">
                    {gapAnalysis.evidence_hierarchy.must_have.map((item, index) => (
                      <li key={index} className="text-gray-300 text-sm">• {item}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-yellow-900/20 border border-yellow-500 p-4 rounded-lg">
                  <h5 className="text-yellow-400 font-semibold mb-3 flex items-center">
                    <span className="mr-2">🟡</span>
                    Should Have
                  </h5>
                  <ul className="space-y-1">
                    {gapAnalysis.evidence_hierarchy.should_have.map((item, index) => (
                      <li key={index} className="text-gray-300 text-sm">• {item}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-green-900/20 border border-green-500 p-4 rounded-lg">
                  <h5 className="text-green-400 font-semibold mb-3 flex items-center">
                    <span className="mr-2">🔵</span>
                    Nice to Have
                  </h5>
                  <ul className="space-y-1">
                    {gapAnalysis.evidence_hierarchy.nice_to_have.map((item, index) => (
                      <li key={index} className="text-gray-300 text-sm">• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Actionable Recommendations */}
          {gapAnalysis.actionable_recommendations && gapAnalysis.actionable_recommendations.length > 0 && (
            <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-white mb-4">🎯 Actionable Recommendations</h4>
              <div className="space-y-2">
                {gapAnalysis.actionable_recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start">
                    <span className="text-blue-400 mr-2 mt-1">•</span>
                    <span className="text-gray-300">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !analyzing
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Affidavit Builder →
        </button>
      </div>
    </div>
  );
};

// USCIS Notice Scanner - Step 8: Affidavit Builder
export const USCISStep8AffidavitBuilder = ({ formData, setFormData, onNext, onBack }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [affidavitContent, setAffidavitContent] = useState('');
  const [savedAffidavits, setSavedAffidavits] = useState(formData.savedAffidavits || []);
  const [customizing, setCustomizing] = useState(false);

  const affidavitTemplates = [
    {
      id: 'personal-declaration',
      title: 'Personal Declaration',
      description: 'Your own statement about the relationship and circumstances',
      icon: '📝',
      template: `PERSONAL DECLARATION IN SUPPORT OF IMMIGRATION APPLICATION

I, [YOUR NAME], hereby declare under penalty of perjury that the following statements are true and correct:

1. BACKGROUND: I am [age] years old and a [citizenship status]. I reside at [address].

2. RELATIONSHIP: I have been married to [SPOUSE NAME] since [DATE]. We met on [DATE] at [LOCATION/CIRCUMSTANCES].

3. BONA FIDE MARRIAGE: Our marriage is genuine and was entered into in good faith, not for immigration purposes. Evidence of our bona fide marriage includes:
   - We share financial responsibilities including joint bank accounts
   - We live together at the same residence
   - We have joint utilities and insurance policies
   - We are recognized as a married couple by friends and family

4. SPECIFIC CIRCUMSTANCES: [Explain any specific issues raised in the RFE/NOID]

5. CONCLUSION: I respectfully request that USCIS approve our application based on the evidence provided.

I declare under penalty of perjury that the foregoing is true and correct.

Date: ___________               Signature: _____________________
                                [YOUR NAME]`
    },
    {
      id: 'witness-affidavit',
      title: 'Friend/Family Witness Affidavit',
      description: 'Template for friends or family members who know your relationship',
      icon: '👥',
      template: `AFFIDAVIT OF [WITNESS NAME] IN SUPPORT OF IMMIGRATION APPLICATION

I, [WITNESS NAME], hereby declare under penalty of perjury that the following statements are true and correct:

1. BACKGROUND: I am [age] years old and a [citizenship status]. I reside at [address]. I have known [APPLICANT NAME] for [duration] and [SPOUSE NAME] for [duration].

2. RELATIONSHIP KNOWLEDGE: I have personal knowledge of the relationship between [APPLICANT] and [SPOUSE]. I first became aware of their relationship on [date/circumstances].

3. OBSERVATIONS: Based on my personal observations, their marriage is genuine because:
   - I have observed them living together as a married couple
   - They demonstrate affection and care for each other
   - They make joint decisions and share responsibilities
   - They participate in social activities together as a couple

4. SPECIFIC EXAMPLES: [Include specific examples of interactions you've witnessed]

5. CONCLUSION: Based on my personal knowledge and observations, I believe their marriage is bona fide and genuine.

I declare under penalty of perjury that the foregoing is true and correct.

Date: ___________               Signature: _____________________
                                [WITNESS NAME]
                                [ADDRESS]
                                [PHONE NUMBER]`
    },
    {
      id: 'circumstantial-explanation',
      title: 'Circumstantial Explanation',
      description: 'Explain specific circumstances or address missing documentation',
      icon: '📋',
      template: `AFFIDAVIT EXPLAINING CIRCUMSTANCES

I, [YOUR NAME], hereby declare under penalty of perjury that the following statements are true and correct:

1. PURPOSE: This affidavit addresses the specific concerns raised in [RFE/NOID] dated [DATE] regarding [SPECIFIC ISSUE].

2. EXPLANATION OF CIRCUMSTANCES: [Detailed explanation of the specific circumstances]

3. REASON FOR MISSING DOCUMENTATION: [Explain why certain documents are not available]

4. ALTERNATIVE EVIDENCE: Although [specific document] is not available, the following evidence demonstrates [what you're trying to prove]:
   - [List alternative evidence]
   - [Explanation of why this evidence is reliable]

5. CONCLUSION: I respectfully request that USCIS consider this explanation and the alternative evidence provided.

I declare under penalty of perjury that the foregoing is true and correct.

Date: ___________               Signature: _____________________
                                [YOUR NAME]`
    },
    {
      id: 'timeline-clarification',
      title: 'Timeline Clarification',
      description: 'Clarify relationship timeline and address any discrepancies',
      icon: '📅',
      template: `AFFIDAVIT CLARIFYING RELATIONSHIP TIMELINE

I, [YOUR NAME], hereby declare under penalty of perjury that the following statements are true and correct:

1. PURPOSE: This affidavit clarifies the timeline of our relationship in response to concerns raised in [RFE/NOID].

2. RELATIONSHIP TIMELINE:
   - [DATE]: First met at [location/circumstances]
   - [DATE]: Began dating relationship
   - [DATE]: Became engaged
   - [DATE]: Married at [location]
   - [DATE]: Began living together (if different from marriage date)

3. CLARIFICATION OF DISCREPANCIES: [Address any specific timeline concerns raised by USCIS]

4. SUPPORTING EVIDENCE: The timeline is supported by:
   - [List specific evidence for each period]
   - [Photos, receipts, travel records, etc.]

5. CONCLUSION: This timeline accurately reflects our relationship progression.

I declare under penalty of perjury that the foregoing is true and correct.

Date: ___________               Signature: _____________________
                                [YOUR NAME]`
    }
  ];

  const generateContent = (templateId) => {
    const template = affidavitTemplates.find(t => t.id === templateId);
    if (!template) return '';

    setCustomizing(true);
    
    // Simulate AI customization based on case context
    setTimeout(() => {
      let customizedContent = template.template;
      
      // Replace placeholders with case-specific information if available
      const parsedResults = formData.parsedResults || {};
      const breakdown = formData.breakdown || {};
      
      if (parsedResults.case_number) {
        customizedContent = customizedContent.replace('[RFE/NOID]', parsedResults.notice_type || 'RFE');
      }
      
      setAffidavitContent(customizedContent);
      setCustomizing(false);
    }, 1500);
  };

  const saveAffidavit = () => {
    if (!affidavitContent.trim()) return;
    
    const newAffidavit = {
      id: Date.now(),
      template: selectedTemplate,
      title: affidavitTemplates.find(t => t.id === selectedTemplate)?.title || 'Custom Affidavit',
      content: affidavitContent,
      created_date: new Date().toISOString(),
      word_count: affidavitContent.split(' ').length
    };
    
    const updatedAffidavits = [...savedAffidavits, newAffidavit];
    setSavedAffidavits(updatedAffidavits);
    setFormData({ ...formData, savedAffidavits: updatedAffidavits });
    
    // Reset form
    setSelectedTemplate('');
    setAffidavitContent('');
  };

  const deleteAffidavit = (affidavitId) => {
    const updatedAffidavits = savedAffidavits.filter(a => a.id !== affidavitId);
    setSavedAffidavits(updatedAffidavits);
    setFormData({ ...formData, savedAffidavits: updatedAffidavits });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📝 Affidavit Builder</h3>
        <p className="text-gray-400 text-lg">Create professional affidavits with AI-powered templates</p>
      </div>

      {/* Template Selection */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📋 Select Affidavit Template</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {affidavitTemplates.map((template) => (
            <button
              key={template.id}
              onClick={() => {
                setSelectedTemplate(template.id);
                generateContent(template.id);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all duration-300 ${
                selectedTemplate === template.id
                  ? 'border-blue-500 bg-blue-900/20'
                  : 'border-gray-600 hover:border-gray-500'
              }`}
            >
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-3">{template.icon}</span>
                <h5 className="text-white font-semibold">{template.title}</h5>
              </div>
              <p className="text-gray-400 text-sm">{template.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Content Editor */}
      {selectedTemplate && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-semibold text-white">
              ✏️ Edit {affidavitTemplates.find(t => t.id === selectedTemplate)?.title}
            </h4>
            {customizing && (
              <div className="flex items-center text-blue-400">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-400 border-t-transparent mr-2"></div>
                Customizing...
              </div>
            )}
          </div>
          
          <textarea
            value={affidavitContent}
            onChange={(e) => setAffidavitContent(e.target.value)}
            placeholder="Affidavit content will appear here..."
            rows="15"
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500 font-mono text-sm"
          />
          
          <div className="flex items-center justify-between mt-4">
            <div className="text-gray-400 text-sm">
              Word count: {affidavitContent.split(' ').filter(word => word.length > 0).length}
            </div>
            <button
              onClick={saveAffidavit}
              disabled={!affidavitContent.trim()}
              className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                affidavitContent.trim()
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              💾 Save Affidavit
            </button>
          </div>
        </div>
      )}

      {/* Saved Affidavits */}
      {savedAffidavits.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">
            📄 Saved Affidavits ({savedAffidavits.length})
          </h4>
          <div className="space-y-3">
            {savedAffidavits.map((affidavit) => (
              <div key={affidavit.id} className="flex items-center justify-between bg-gray-700 p-4 rounded-lg">
                <div>
                  <p className="text-white font-medium">{affidavit.title}</p>
                  <p className="text-gray-400 text-sm">
                    {affidavit.word_count} words • Created {new Date(affidavit.created_date).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => deleteAffidavit(affidavit.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          Continue to Cover Letter →
        </button>
      </div>
    </div>
  );
};